import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ActionContainer from '../../../../components/ActionContainer';
import Button from '../../../../components/Button';
import ErrorMessage, { useError } from '../../../../components/ErrorMessage';
import { login, sendPasswordResetEmail } from '../../../../lib/authentication';
import { UserFacingError } from '../../../../lib/errors';
import * as types from '../../../../types';
import styles from './InlineLoginForm.module.scss';
import eyeOpen from '../../../../assets/images/eye-open.svg';
import eyeClosed from '../../../../assets/images/eye-closed.svg';

interface InlineLoginFormProps {
  email: string;
  onLoginSuccess: (session: types.Session) => void;
  showTitle?: boolean;
  loginFn?: (email: string, password: string) => Promise<types.Session>;
}

export default function InlineLoginForm({ email, onLoginSuccess, showTitle = true, loginFn }: InlineLoginFormProps) {
  const { t } = useTranslation();
  const formRef = useRef<HTMLFormElement>(null);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loginFailed, setLoginFailed] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [sendingReset, setSendingReset] = useState(false);
  const { error, errorID, setError, clearError } = useError();

  const handleLogin = useCallback(async () => {
    if (submitting) return;

    setSubmitting(true);

    try {
      const doLogin = loginFn ?? login;
      const session = await doLogin(email, password);
      onLoginSuccess(session);
    } catch (err) {
      setSubmitting(false);
      setLoginFailed(true);
      if (err instanceof UserFacingError) {
        setError(err.message);
      } else {
        setError(t('an_unknown_error_occurred'));
      }
    }
  }, [email, password, submitting, onLoginSuccess, setError, t, loginFn]);

  const handleSubmit = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      void handleLogin();
    },
    [handleLogin],
  );

  const handleSendResetEmail = useCallback(async () => {
    if (sendingReset) return;
    setSendingReset(true);
    try {
      await sendPasswordResetEmail(email);
      setResetEmailSent(true);
      clearError();
    } catch {
      setError(t('an_unknown_error_occurred'));
    } finally {
      setSendingReset(false);
    }
  }, [email, sendingReset, setError, clearError, t]);

  const buttonText = submitting ? t('logging_in_ellipsis') : t('log_in_and_continue');

  return (
    <div className={styles.wrapper}>
      <div className={styles.container}>
        {showTitle && <h2 className={styles.title}>{t('welcome_back')}</h2>}

        <div className={styles.subtitleWrapper}>
          <div className={styles.subtitle}>{t('login_required_subtitle')}</div>
        </div>

        <form onSubmit={handleSubmit} ref={formRef} className={styles.form}>
          <label className={styles.fieldLabel}>{t('email')}</label>
          <div className={styles.emailDisplay}>{email}</div>

          <label className={styles.fieldLabel}>{t('password')}</label>
          <div className={styles.passwordInputWrapper}>
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t('enter_password')}
              autoComplete="current-password"
              className={styles.passwordInput}
              autoFocus
            />
            <button
              type="button"
              className={styles.passwordToggle}
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? t('hide_password') : t('show_password')}
              aria-pressed={showPassword}
            >
              <img src={showPassword ? eyeClosed : eyeOpen} alt="" />
            </button>
          </div>
        </form>

        {resetEmailSent && (
          <div className={styles.resetEmailSent}>{t('check_your_email_for_a_link_to_reset_your_password')}</div>
        )}
      </div>

      <div className={styles.actionContainer}>
        <ActionContainer className={styles.actionButtons}>
          <Button
            onClick={(evt) => {
              evt.preventDefault();
              void handleLogin();
            }}
            disabled={submitting || !password}
          >
            {buttonText}
          </Button>
        </ActionContainer>
        {error && <ErrorMessage errors={[error]} errorID={errorID} />}
        {loginFailed && !resetEmailSent ? (
          <button
            type="button"
            onClick={() => void handleSendResetEmail()}
            disabled={sendingReset}
            className={styles.forgotPassword}
          >
            {sendingReset ? t('sending_ellipsis') : t('send_password_reset_email')}
          </button>
        ) : (
          <button
            type="button"
            onClick={() => void handleSendResetEmail()}
            disabled={sendingReset}
            className={styles.forgotPassword}
          >
            {t('forgot_your_password')}
          </button>
        )}
      </div>
    </div>
  );
}
